import { ProductModifier, Country, Brand, Product, } from "@busdk/storefront-service/dist/models/enums";
import { CountryCode, Occupation, } from "@busdk/credit-decision-service/dist/client/src/models/enums";
import axios from "axios";
import { Route } from "../../../common/Navigation";
import { parseEuroAmount } from "../../../functional/normalize";
import { wait } from "../../../common/wait";
import * as H from 'history';
import { AdditionalAppFormDataType } from '../../../pages/AdditionalAppForm/AdditionalAppFormPage';

const {
  REACT_APP_SOFT_CREDIT_CHECK_URL,
  REACT_APP_COUNTRY,
  REACT_APP_BRAND,
  REACT_APP_PRODUCT
} = process.env;

export interface ISoftCreditCheckResult {
  isEligible: boolean;
}

export interface ISoftCreditCheckBaseEvent {
  transaction: {
    id: string;
    country: Country.DE;
    product: Product.AUTOCREDIT | Product.PRIVATELEASE;
    brand: Brand.VW | Brand.VWN | Brand.AUDI | Brand.VWFS | Brand.SKODA | Brand.SEAT;
    productmodifier: ProductModifier;
  };
}

export interface IDacSoftCreditCheckEvent extends ISoftCreditCheckBaseEvent {
  dacData: {
    digitalAccountCheckTransactionId: string;
  };
}

export interface IManualSoftCreditCheckEvent extends ISoftCreditCheckBaseEvent {
  formData: {
    occupation: Occupation;
    employmentEndDate: string;
    permanentlyEmployed: boolean;
    totalMonthlyExpenses: number;
    monthlyNetIncomeAmount: number;
    countryCode: CountryCode;
  };
}

export type ISoftCreditCheckEvent = IDacSoftCreditCheckEvent | IManualSoftCreditCheckEvent;

/**
 * Redirect for both, DAC and manual soft credit check
 * @param history react router history
 * @param creditCheckResult 
 */
export const redirectBasedOnCreditCheckResult = (history: H.History, creditCheckResult: ISoftCreditCheckResult) => {
  if (creditCheckResult.isEligible) {
    history.push(Route.SoftCreditApproved);
  } else {
    history.push(Route.SoftCreditRefused);
  }
}

export const performFormbasedSoftCreditCheck = async (
  {
    formData: data, transactionId, productmodifier
  }: {
    formData: AdditionalAppFormDataType;
    transactionId: string;
    productmodifier: ProductModifier.USED | ProductModifier.NEW;
  }
): Promise<{ isEligible: boolean }> => {
  const request: IManualSoftCreditCheckEvent = {
    formData: {
      employmentEndDate: data.employmentLimit, // TODO: validate date transformation
      monthlyNetIncomeAmount: parseEuroAmount(data.netIncome),
      occupation: data.professionGroup as Occupation,
      permanentlyEmployed: data.employment === 'unlimited',
      totalMonthlyExpenses: parseEuroAmount(data.monthlyExpenses),
      countryCode: data.country
    },
    transaction: {
      id: transactionId,
      brand: REACT_APP_BRAND as any,
      country: REACT_APP_COUNTRY as Country.DE,
      product: REACT_APP_PRODUCT as any,
      productmodifier: productmodifier,
    }
  }

  return performSoftCreditCheck(request);
}

export const performDacSoftCreditCheck = async (
  {
    transactionId,
    digitalAccountCheckTransactionId,
    productmodifier,
  }: {
    transactionId: string,
    digitalAccountCheckTransactionId: string,
    productmodifier: ProductModifier.USED | ProductModifier.NEW
  },
): Promise<{ isEligible: boolean }> => {
  const request: IDacSoftCreditCheckEvent = {
    dacData: { digitalAccountCheckTransactionId },
    transaction: {
      id: transactionId,
      country: REACT_APP_COUNTRY as Country.DE,
      brand: REACT_APP_BRAND as any,
      product: Product.AUTOCREDIT,
      productmodifier: productmodifier,
    }
  };

  return performSoftCreditCheck(request);
};

/**
 * Actually execute the POST to lambda
 * @param request the data for the request
 */
const performSoftCreditCheck = async (request: ISoftCreditCheckEvent): Promise<{ isEligible: boolean }> => {
  const axiosPromise = axios.post<{ isEligible: boolean }>(REACT_APP_SOFT_CREDIT_CHECK_URL, request);
  const response = await takeMinimumTimeToResolve(axiosPromise, 3000); // make request take at least 3 seconds so loading spinner is shown as specified
  return response.data;
};

const takeMinimumTimeToResolve = <T>(promise: Promise<T>, milliSeconds: number) => {
  return Promise.all([promise, wait(milliSeconds)]).then(all => all[0])
}

